import { Platform } from 'react-native';
import { uploadFileManager } from './uploadFile';
import { showToast } from '../components/Toast';
import { getLanguage } from '../language/i18n';
import VersionVal from "../../package.json";
import { getToken } from '../store/token';
import { config_api_proxy_host } from '../../imsdk/api';

// Web 版本使用原生 fetch，非 Web 使用 react-native-blob-util
let ReactNativeBlobUtil: any;
if (Platform.OS !== 'web') {
    ReactNativeBlobUtil = require('react-native-blob-util').default;
}

/*
头像类的  不管是人头像 还是群组头像都用  profile
聊天所有相关的数据  都用 chat
朋友圈（那个个人动态）的  用 moments
图标类的  用 icon
表情的 用 emoticon
系统后台的聊天下载 用 download
其他暂时没考虑到的  就用  global
*/
export enum UploadKey {
    Chat = "chat",
    Global = "global",
    Icon = "icon",
    Moments = "moments",
    Profile = "profile",
    Emoticon = "emoticon"
}

type ReactBlobUtilOption = {
    path: string,
    filetype: string,
    fileName: string,
    key: UploadKey,
    handleKey?: string,
    mimeType?: string,
    fileSize?: number
}

// Web 版本：将 data URL 转换为 Blob
function dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
}

class ReactBlobUtil {
    static async upload(option: ReactBlobUtilOption): Promise<{ code: number, msg?: string, url?: string, path?: string }> {
        const url = config_api_proxy_host() + "/v1/fs/uploadaws"
        const handleKey = option.handleKey
        const pathMeta = {
            isDataUrl: option.path?.startsWith('data:') === true,
            isBlobUrl: option.path?.startsWith('blob:') === true,
            pathLength: option.path?.length || 0,
        };

        if (handleKey) {
            uploadFileManager.addProgress(handleKey)
        }

        // Web 版本使用 fetch API
        try {
            // 将 data URL 转换为 Blob
            let blob: Blob;
            if (option.path.startsWith('data:')) {
                blob = dataURLtoBlob(option.path);
                console.log('[ReactBlobUtil] Web 上传 - data URL 转换为 Blob, size:', blob.size, 'type:', blob.type);
            } else {
                // 如果是其他格式的路径，尝试 fetch
                const response = await fetch(option.path);
                blob = await response.blob();
                console.log('[ReactBlobUtil] Web 上传 - fetch 获取 Blob, size:', blob.size, 'type:', blob.type);
            }

            // 创建 FormData
            const formData = new FormData();
            formData.append('key', option.key);
            formData.append('ext', option.filetype);
            formData.append('files', blob, `${option.fileName}.${option.filetype}`);
            console.log('[ReactBlobUtil] Web 上传 - FormData 创建完成, key:', option.key, 'ext:', option.filetype, 'fileName:', option.fileName);

            // 使用 XMLHttpRequest 来支持上传进度
            return new Promise((resolve) => {
                const xhr = new XMLHttpRequest();

                // 上传进度
                xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable && handleKey) {
                            const percentage = Math.round((e.loaded / e.total) * 100);
                            uploadFileManager.handleListener(handleKey, percentage);
                        }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status === 200) {
                        try {
                            const res = JSON.parse(xhr.responseText);
                            console.log("上传成功", res, xhr.responseText);
                            const firstUrl = res?.data?.urls?.[0];
                            const errs = Array.isArray(res?.data?.errs) ? res.data.errs : [];
                            const firstErr = errs.find((v: any) => typeof v === 'string' && v.length > 0);
                            // 后端可能返回 code=0，但 urls 为空且 errs 有内容，这里按失败处理，避免上层继续发空 URL 消息。
                            if (res.code == 0 && typeof firstUrl === 'string' && firstUrl.length > 0 && !firstErr) {
                                if (handleKey) {
                                    uploadFileManager.handleSucListener(handleKey)
                                }
                                resolve({ code: 0, url: firstUrl, path: option.path });
                            } else {
                                if (handleKey) {
                                    uploadFileManager.handleFailListener(handleKey)
                                }
                                const failMsg = firstErr || res.msg || '文件上传失败';
                                console.error('[BEE-FILE][UPLOAD-FAIL-DETAIL]', {
                                    uploadKey: option.key,
                                    ext: option.filetype,
                                    mimeType: option.mimeType || blob.type || '',
                                    fileSize: option.fileSize ?? blob.size ?? 0,
                                    handleKey,
                                    ...pathMeta,
                                    httpStatus: xhr.status,
                                    backendCode: res?.code,
                                    backendMsg: res?.msg,
                                    urls: res?.data?.urls,
                                    errs: res?.data?.errs,
                                });
                                showToast(failMsg);
                                resolve({ code: -1, msg: failMsg });
                            }
                        } catch (e) {
                            console.error("JSON 解析失败", e, xhr.responseText);
                            if (handleKey) {
                                uploadFileManager.handleFailListener(handleKey)
                            }
                            resolve({ code: -1, msg: '解析失败' });
                        }
                    } else {
                        if (handleKey) {
                            uploadFileManager.handleFailListener(handleKey)
                        }
                        console.error('[BEE-FILE][UPLOAD-HTTP-FAIL]', {
                            uploadKey: option.key,
                            ext: option.filetype,
                            mimeType: option.mimeType || blob.type || '',
                            fileSize: option.fileSize ?? blob.size ?? 0,
                            handleKey,
                            ...pathMeta,
                            httpStatus: xhr.status,
                            responseText: xhr.responseText,
                        });
                        resolve({ code: -1, msg: `上传失败: ${xhr.status}` });
                    }
                });

                xhr.addEventListener('error', () => {
                    if (handleKey) {
                        uploadFileManager.handleFailListener(handleKey)
                    }
                    console.error('[BEE-FILE][UPLOAD-NETWORK-FAIL]', {
                        uploadKey: option.key,
                        ext: option.filetype,
                        mimeType: option.mimeType || blob.type || '',
                        fileSize: option.fileSize ?? blob.size ?? 0,
                        handleKey,
                        ...pathMeta,
                    });
                    resolve({ code: -1, msg: '网络错误' });
                });

                xhr.open('POST', url);
                xhr.setRequestHeader('KK-Version', Platform.OS + '.' + VersionVal.version);
                // console.log("上传设置 header", 'KK-Version', Platform.OS + '.' + VersionVal.version);
                xhr.setRequestHeader('lang', getLanguage());
                xhr.setRequestHeader('X-Token', getToken());
                xhr.send(formData);
            });
        } catch (err: any) {
            if (handleKey) {
                uploadFileManager.handleFailListener(handleKey)
            }
            alert('上传失败: ' + (err?.message || '未知错误'));
            return { code: -1, msg: err?.message || '上传失败' };
        }

    }
}
export default ReactBlobUtil